<template>
  <div class="spinning-wheel-campaign-form">
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <div class="text-right">
          <p class="d-inline-flex">
            <a class="btn btn-danger btn-icon delete mar-rt-10" v-if="campaignId" @click.prevent="handleDelete">
              <img src="/static/img/icons/icon-close.png">
              {{ $t('general.delete') }}
            </a>
            <button type="button" @click.prevent="save" class="btn btn-icon btn-success mar-rt-10 save">
              <img src="/static/img/icons/save.png">
              {{ $t('general.save') }}
            </button>
          </p>
        </div>
      </div>
    </div>

    <div :class="(loading ? 'show-loader' : 'hide-loader')">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <h5>{{ $t('spinningWheel.generalSettings') }}</h5>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('spinningWheel.campaignTitle') }}</label>
              <input
                v-model="model.title"
                type="text"
                class="form-control"
                :placeholder="$t('spinningWheel.campaignTitle')"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('spinningWheel.description') }}</label>
              <textarea
                v-model="model.description"
                rows="3"
                class="form-control"
                :placeholder="$t('spinningWheel.description')"
              ></textarea>
            </div>
            <div class="form-group">
              <p-switch v-model="model.active" type="success"></p-switch>
              <label class="mar-lt-10">{{ $t('spinningWheel.active') }}</label>
              <small class="display-block text-muted">{{ $t('spinningWheel.activeRequires100') }}</small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('spinningWheel.availability') }}</label>
              <el-date-picker
                v-model="availabilityRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                format="dd/MM/yyyy"
                style="width:100%"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('spinningWheel.minOrderValue') }}</label>
              <input v-model.number="model.minimum_order_value" type="number" min="0" step="0.01" class="form-control" />
            </div>
            <div class="form-group">
              <searchable-multi-select
                v-model="locationsFormatted"
                label="spinningWheel.locations"
                url="/get-locations"
                :initial-limit="1000"
                filter-locally
                :placeholder="$t('general.all')"
              />
              <small class="text-muted">
                <i class="fa fa-info-circle" aria-hidden="true"></i> {{ $t('spinningWheel.locationsInfo') }}
              </small>
            </div>
            <div class="form-group" v-if="rewards.length">
              <label>{{ $t('spinningWheel.percentages') }}</label>
              <p>
                <strong :class="percentagesValid ? 'text-success' : 'text-danger'">
                  {{ formatPercentage(percentageSum) }}%
                </strong>
                <span v-if="!percentagesValid" class="text-danger"> — {{ $t('spinningWheel.mustTotal100') }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <div class="display-flex" style="justify-content: space-between; align-items: center;">
          <h5 class="mar-bt-0">{{ $t('spinningWheel.rewards') }}</h5>
          <button
            type="button"
            class="btn add"
            @click="openRewardForm()"
          >
            <img src="/static/img/icons/add.png">
            {{ $t('spinningWheel.addReward') }}
          </button>
        </div>

        <p class="text-muted mar-to-10">{{ $t('spinningWheel.sortOrderHelp') }}</p>

        <el-table
          ref="rewardsTable"
          class="spinning-wheel-rewards-table"
          :data="visibleRewards"
          :empty-text="$t('gestiune.noData')"
          style="width: 100%; margin-top: 15px;"
        >
          <el-table-column
            class-name="centered-cell"
            :label="$t('spinningWheel.sortOrder')"
            :min-width="90"
            align="center"
            header-align="center"
          >
            <template slot-scope="props">
              <input
                type="number"
                min="0"
                max="1000"
                step="1"
                class="form-control"
                v-model.number="props.row.sort_order"
                @input="onSortOrderInput(props.row)"
                @click.stop
              />
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            v-if="campaignId"
            prop="id"
            :label="$t('customers.id')"
            :min-width="60"
            align="center"
            header-align="center"
          />
          <el-table-column
            class-name="centered-cell"
            :label="$t('spinningWheel.reward')"
            min-width="140"
            align="center"
            header-align="center"
            show-overflow-tooltip
          >
            <template slot-scope="props">
              {{ displayRewardTitle(props.row) }}
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            :label="$t('spinningWheel.rewardType')"
            min-width="130"
            align="center"
            header-align="center"
          >
            <template slot-scope="props">
              {{ rewardTypeLabel(props.row) }}
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            :label="$t('spinningWheel.winningPercentage')"
            min-width="120"
            align="center"
            header-align="center"
          >
            <template slot-scope="props">
              <div class="display-flex" style="align-items: center; justify-content: center;">
                <input
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  class="form-control"
                  style="max-width: 90px;"
                  v-model.number="props.row.winning_percentage"
                  :disabled="Number(props.row.type) === 1"
                  @input="onPercentageInput(props.row)"
                  @click.stop
                />
                <span class="mar-lt-5">%</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            :label="$t('spinningWheel.active')"
            min-width="80"
            align="center"
            header-align="center"
          >
            <template slot-scope="props">
              <p-switch
                :value="!!props.row.active"
                type="success"
                :disabled="Number(props.row.type) === 1 || rewardActiveSaving"
                :aria-disabled="Number(props.row.type) === 1 || rewardActiveSaving"
                :class="{ 'reward-active-toggle--disabled': Number(props.row.type) === 1 || rewardActiveSaving }"
                @input="value => handleRewardActive(props.row, value)"
                @click.native.stop
              />
            </template>
          </el-table-column>
          <el-table-column
            class-name="centered-cell"
            :label="$t('spinningWheel.actions')"
            :width="120"
            align="center"
            header-align="center"
          >
            <template slot-scope="props">
              <el-dropdown trigger="click" placement="bottom-end" @click.native.stop>
                <span class="arrow-box">&#9662;</span>
                <el-dropdown-menu slot="dropdown" class="custom-dropdown-menu">
                  <el-dropdown-item
                    v-if="props.row.open_campaign_id || props.row.generated_campaign_id"
                    @click.native.stop="openGeneratedCampaign(props.row)"
                  >
                    {{ $t('spinningWheel.openCampaign') }}
                  </el-dropdown-item>
                  <el-dropdown-item @click.native.stop="openRewardForm(props.row)">
                    {{ $t('general.edit') }}
                  </el-dropdown-item>
                  <el-dropdown-item v-if="Number(props.row.type) !== 1" @click.native.stop="duplicateReward(props.row)">
                    {{ $t('spinningWheel.duplicate') }}
                  </el-dropdown-item>
                  <el-dropdown-item @click.native.stop="deleteReward(props.row)">
                    {{ $t('general.delete') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <el-dialog
      :title="editingReward ? $t('spinningWheel.editReward') : $t('spinningWheel.addReward')"
      :visible.sync="rewardDialogVisible"
      width="720px"
      top="4vh"
      custom-class="spinning-wheel-reward-dialog"
      append-to-body
      :modal-append-to-body="true"
      :close-on-click-modal="false"
      :z-index="100000"
    >
      <div class="spinning-wheel-reward-dialog__body">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('spinningWheel.rewardType') }}</label>
              <select v-model.number="rewardForm.type" class="form-control">
                <option :value="1" :disabled="hasOtherNothing">{{ $t('spinningWheel.typeNothing') }}</option>
                <option :value="2">{{ $t('spinningWheel.typeLoyaltyPoints') }}</option>
                <option :value="3">{{ $t('spinningWheel.typeDiscountCoupon') }}</option>
                <option :value="4">{{ $t('spinningWheel.typeFreeProduct') }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('spinningWheel.winningPercentage') }}</label>
              <input v-model.number="rewardForm.winning_percentage" :disabled="rewardForm.type === 1" type="number" min="0.0001" step="0.0001" class="form-control" />
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('spinningWheel.displayTitle') }}</label>
          <input
            v-model="rewardForm.display_title"
            type="text"
            class="form-control"
            :placeholder="$t('spinningWheel.displayTitle')"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('spinningWheel.displayDescription') }}</label>
          <textarea
            v-model="rewardForm.display_description"
            rows="3"
            class="form-control"
            :placeholder="$t('spinningWheel.displayDescription')"
          ></textarea>
        </div>

        <div class="form-group" v-if="rewardForm.type === 2">
          <label>{{ $t('spinningWheel.loyaltyPoints') }}</label>
          <input v-model.number="rewardForm.loyalty_points" type="number" min="1" step="1" class="form-control" />
        </div>

        <template v-if="rewardForm.type === 3">
          <div class="alert alert-info">{{ $t('spinningWheel.autoGenerateNotice') }}</div>
          <div class="form-group">
            <label>{{ $t('spinningWheel.couponAppliesTo') }}</label>
            <select v-model="rewardForm.usage_type" class="form-control">
              <option value="PRODUCTS">{{ $t('spinningWheel.couponProducts') }}</option>
              <option value="SHIPPING">{{ $t('spinningWheel.couponDelivery') }}</option>
              <option value="TAX">{{ $t('spinningWheel.couponTaxes') }}</option>
            </select>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('spinningWheel.couponType') }}</label>
                <select v-model="rewardForm.coupon_type" class="form-control">
                  <option value="PERCENTAGE">{{ $t('spinningWheel.couponPercentage') }}</option>
                  <option value="FIXED">{{ $t('spinningWheel.couponFixed') }}</option>
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>{{ $t('spinningWheel.discount') }}</label>
                <input v-model.number="rewardForm.discount" type="number" min="0.01" step="0.01" class="form-control" />
              </div>
            </div>
          </div>
        </template>

        <template v-if="rewardForm.type === 4">
          <div class="alert alert-info">{{ $t('spinningWheel.autoGenerateNotice') }}</div>
          <div class="form-group">
            <label>{{ $t('spinningWheel.product') }}</label>
            <el-select
              v-model="rewardForm.catalogproduct_id"
              filterable
              remote
              clearable
              :remote-method="searchProducts"
              :loading="productsLoading"
              style="width: 100%;"
              :placeholder="$t('spinningWheel.searchProduct')"
              @visible-change="onProductDropdown"
            >
              <el-option
                v-for="p in productOptions"
                :key="p.id"
                :label="p.name + (p.price != null ? ' (' + p.price + ')' : '')"
                :value="p.id"
              />
            </el-select>
          </div>
        </template>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('spinningWheel.sortOrder') }}</label>
              <input v-model.number="rewardForm.sort_order" type="number" min="0" step="1" class="form-control" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mar-to-25">
              <p-switch v-if="rewardForm.type !== 1" v-model="rewardForm.active" type="success"></p-switch>
              <label class="mar-lt-10">{{ $t('spinningWheel.active') }}</label>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer spinning-wheel-reward-dialog__footer">
        <a href="#" class="btn btn-danger btn-icon delete" @click.prevent="rewardDialogVisible = false">
          <img src="/static/img/icons/icon-close.png">
          {{ $t('general.cancel') }}
        </a>
        <button class="btn btn-success btn-icon save" type="button" :disabled="rewardSaving" @click="saveReward">
          <img src="/static/img/icons/save.png">
          {{ $t('general.save') }}
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import swal from 'sweetalert2'
import { DatePicker, Select, Option, Dialog, Table, TableColumn, Dropdown, DropdownMenu, DropdownItem } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import SearchableMultiSelect from 'src/components/UIComponents/Inputs/SearchableMultiSelect.vue'
import { rebalanceRewards, rewardTitle } from './rewards'

Vue.use(DatePicker)
Vue.use(Select)
Vue.use(Option)
Vue.use(Dialog)
Vue.use(Table)
Vue.use(TableColumn)
Vue.use(Dropdown)
Vue.use(DropdownMenu)
Vue.use(DropdownItem)

export default {
  components: {
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    PSwitch,
    SearchableMultiSelect
  },
  props: {
    route: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      loading: false,
      campaignId: null,
      availabilityRange: null,
      percentageSum: '0',
      percentagesValid: false,
      rewards: [],
      locations: [],
      locationsFormatted: [],
      sortOrderDebounce: null,
      percentageDebounce: null,
      pendingSortUpdates: {},
      pendingPercentageUpdates: {},
      model: {
        title: '',
        description: '',
        active: false,
        minimum_order_value: 0,
        location_ids: []
      },
      rewardDialogVisible: false,
      editingReward: null,
      rewardForm: this.emptyRewardForm(),
      productOptions: [],
      productsLoading: false,
      productSearchVersion: 0,
      nextDraftId: -1,
      rewardSaving: false,
      rewardActiveSaving: false
    }
  },
  created () {
    const fromProp = this.route && this.route.params && this.route.params.id
    const fromRouter = this.$route && this.$route.params && this.$route.params.id
    this.campaignId = fromProp || fromRouter || null
    this.getLocations().then(() => {
      if (this.campaignId) this.loadCampaign()
    }).catch(() => {
      if (this.campaignId) this.loadCampaign()
    })
  },
  computed: {
    visibleRewards () {
      return this.rewards.filter(row => Number(row.type) !== 1 || Number(row.winning_percentage) > 0)
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || (Number(a.id) || 0) - (Number(b.id) || 0))
    },
    hasOtherNothing () {
      return this.rewards.some(row => Number(row.type) === 1 && (!this.editingReward || row.id !== this.editingReward.id))
    }
  },
  beforeDestroy () {
    clearTimeout(this.sortOrderDebounce)
    clearTimeout(this.percentageDebounce)
    this.productSearchVersion++
  },
  methods: {
    displayRewardTitle (row) {
      return rewardTitle(row, this.$t('spinningWheel.typeNothing')) || this.rewardTypeLabel(row)
    },
    setDraftRewards (rows, createIfMissing = true) {
      try {
        this.rewards = rebalanceRewards(rows, createIfMissing)
        this.percentageSum = this.rewards.filter(row => row.active)
          .reduce((sum, row) => sum + Math.round(Number(row.winning_percentage) * 10000), 0) / 10000
        this.percentagesValid = this.percentageSum === 100
        return true
      } catch (error) {
        swal('', this.$t(error.message), 'error').catch(() => {})
        return false
      }
    },
    handleRewardActive (row, value) {
      if (Number(row.type) === 1 || this.rewardActiveSaving) return
      const active = !!value
      const previous = !!row.active
      if (active === previous) return
      if (!this.campaignId) {
        this.setDraftRewards(this.rewards.map(item => item.id === row.id ? { ...item, active } : item))
        return
      }
      this.rewardActiveSaving = true
      row.active = active
      const body = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        active: active ? 1 : 0
      }
      this.apiRequest('/spinning-wheel-rewards/' + row.id, 'PUT', body, response => {
        this.rewardActiveSaving = false
        if (!response.status) {
          row.active = previous
          swal('', this.flattenErrors(response.errors) || this.$t('validations.wentWrong'), 'error').catch(() => {})
          return
        }
        if (response.campaign) this.applyCampaignMeta(response.campaign)
        else this.loadCampaign()
      })
    },
    getLocations () {
      return new Promise((resolve, reject) => {
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/get-locations', 'GET', data, (response) => {
          if (response.status) {
            this.locations = response.data || []
            resolve()
          } else {
            reject(new Error('Failed to fetch locations'))
          }
        })
      })
    },
    syncLocationsFormatted (locationIds) {
      const ids = (locationIds || []).map((id) => Number(id))
      this.locationsFormatted = (this.locations || []).filter((loc) => ids.includes(Number(loc.id)))
    },
    emptyRewardForm () {
      return {
        type: 2,
        winning_percentage: 0,
        display_title: '',
        display_description: '',
        loyalty_points: null,
        coupon_type: 'PERCENTAGE',
        usage_type: 'PRODUCTS',
        discount: null,
        catalogproduct_id: null,
        sort_order: 0,
        active: true
      }
    },
    rewardTypeLabel (row) {
      const map = {
        1: this.$t('spinningWheel.typeNothing'),
        2: this.$t('spinningWheel.typeLoyaltyPoints'),
        3: this.$t('spinningWheel.typeDiscountCoupon'),
        4: this.$t('spinningWheel.typeFreeProduct')
      }
      return map[Number(row.type)] || row.type_label || '—'
    },
    toDateOnly (value) {
      if (value == null || value === '') return null
      const s = String(value).trim()
      return s.length >= 10 ? s.slice(0, 10) : s
    },
    formatPercentage (value) {
      if (value == null || value === '') return '0'
      const n = Number(value)
      if (Number.isNaN(n)) {
        const s = String(value)
        return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') || '0'
      }
      const fixed = n.toFixed(4)
      return fixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
    },
    flattenErrors (errors) {
      if (!errors) return null
      if (typeof errors === 'string') return errors
      const parts = []
      Object.keys(errors).forEach((key) => {
        const val = errors[key]
        if (Array.isArray(val)) {
          val.forEach((m) => { if (m) parts.push(String(m)) })
        } else if (val && typeof val === 'object') {
          Object.keys(val).forEach((k) => {
            const inner = val[k]
            if (Array.isArray(inner)) inner.forEach((m) => { if (m) parts.push(String(m)) })
            else if (inner) parts.push(String(inner))
          })
        } else if (val) {
          parts.push(String(val))
        }
      })
      return parts.length ? parts.join('\n') : null
    },
    applyCampaignMeta (item) {
      if (!item) return
      if (item.percentage_sum != null) this.percentageSum = item.percentage_sum
      if (item.percentages_valid != null) this.percentagesValid = !!item.percentages_valid
      if (item.active != null) this.model.active = !!item.active
      if (item.rewards) {
        this.rewards = item.rewards
        this.$nextTick(() => this.layoutRewardsTable())
      }
    },
    backButton () {
      this.$bus.$emit('close-modal')
    },
    loadCampaign () {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/spinning-wheel-campaigns/' + this.campaignId, 'GET', data, (response) => {
        this.loading = false
        if (!response.status || !response.data) {
          swal('', this.$t('validations.wentWrong'), 'error')
          return
        }
        const item = response.data
        this.model.title = item.title || ''
        this.model.description = item.description || ''
        this.model.active = !!item.active
        this.model.minimum_order_value = item.minimum_order_value != null ? Number(item.minimum_order_value) : 0
        this.model.location_ids = item.location_ids || []
        this.syncLocationsFormatted(this.model.location_ids)
        this.percentageSum = item.percentage_sum || '0'
        this.percentagesValid = !!item.percentages_valid
        const from = this.toDateOnly(item.starts_at)
        const to = this.toDateOnly(item.ends_at)
        this.availabilityRange = (from && to) ? [from, to] : null
        this.rewards = item.rewards || []
        this.$nextTick(() => this.layoutRewardsTable())
      })
    },
    layoutRewardsTable () {
      const table = this.$refs.rewardsTable
      if (table && typeof table.doLayout === 'function') table.doLayout()
    },
    scrollToRewards () {
      const el = this.$el && this.$el.querySelector
        ? this.$el.querySelector('.spinning-wheel-rewards-table')
        : null
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    payload () {
      const range = this.availabilityRange || []
      const locationIds = (this.locationsFormatted || []).map((loc) => Number(loc.id))
      return {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        title: this.model.title,
        description: this.model.description,
        active: this.model.active ? 1 : 0,
        starts_at: range[0] || null,
        ends_at: range[1] || null,
        minimum_order_value: this.model.minimum_order_value != null ? this.model.minimum_order_value : 0,
        location_ids: locationIds,
        ...(!this.campaignId ? { rewards: this.rewards.map(row => ({
          type: row.type,
          winning_percentage: row.winning_percentage,
          display_title: row.display_title,
          display_description: row.display_description,
          loyalty_points: row.loyalty_points,
          coupon_type: row.coupon_type,
          discount: row.discount,
          usage_type: Number(row.type) === 3 ? (row.usage_type || 'PRODUCTS') : 'PRODUCTS',
          catalogproduct_id: row.catalogproduct_id,
          sort_order: row.sort_order,
          active: row.active ? 1 : 0
        })) } : {})
      }
    },
    save () {
      if (!this.model.title || !String(this.model.title).trim()) {
        swal('', this.$t('spinningWheel.titleRequired'), 'error')
        return
      }
      const range = this.availabilityRange || []
      if (!range[0] || !range[1]) {
        swal('', this.$t('spinningWheel.datesRequired'), 'error')
        return
      }
      if (this.loading) return
      this.loading = true
      const body = this.payload()
      const done = (response) => {
        this.loading = false
        if (!response.status) {
          const msg = this.flattenErrors(response.errors) || this.$t('validations.wentWrong')
          swal('', msg, 'error')
          if (response.data && response.data.id && !this.campaignId) {
            this.campaignId = response.data.id
            this.rewards = response.data.rewards || []
          }
          return
        }
        swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
        this.$bus.$emit('refresh')
        if (!this.campaignId && response.data && response.data.id) {
          this.campaignId = response.data.id
          this.applyCampaignMeta(response.data)
          this.$nextTick(() => {
            this.layoutRewardsTable()
            this.scrollToRewards()
          })
        } else if (this.campaignId) {
          this.loadCampaign()
        }
      }
      if (this.campaignId) {
        this.apiRequest('/spinning-wheel-campaigns/' + this.campaignId, 'PUT', body, done)
      } else {
        this.apiRequest('/spinning-wheel-campaigns', 'POST', body, done)
      }
    },
    handleDelete () {
      swal({
        title: this.$t('spinningWheel.confirmDelete'),
        useRejections: false,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no')
      }).then((result) => {
        if (!result || !result.value) return
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/spinning-wheel-campaigns/' + this.campaignId, 'DELETE', data, (response) => {
          if (response.status) {
            this.$bus.$emit('refresh')
            this.$bus.$emit('close-modal')
          } else {
            swal('', this.$t('validations.wentWrong'), 'error')
          }
        })
      })
    },
    openRewardForm (row) {
      this.productSearchVersion++
      this.editingReward = row || null
      if (row) {
        this.rewardForm = {
          type: Number(row.type) || 1,
          winning_percentage: Number(row.winning_percentage) || 0,
          display_title: this.displayRewardTitle(row) || '',
          display_description: row.display_description || '',
          loyalty_points: row.loyalty_points,
          coupon_type: row.coupon_type || 'PERCENTAGE',
          usage_type: row.usage_type || 'PRODUCTS',
          discount: row.discount,
          catalogproduct_id: row.catalogproduct_id || null,
          sort_order: row.sort_order || 0,
          active: !!row.active
        }
        this.productOptions = row.product ? [row.product] : []
      } else {
        this.rewardForm = this.emptyRewardForm()
        this.productOptions = []
      }
      this.rewardDialogVisible = true
      if (this.rewardForm.type === 4) this.searchProducts('')
    },
    onProductDropdown (visible) {
      if (visible && !this.productOptions.length) this.searchProducts('')
    },
    searchProducts (query) {
      const version = ++this.productSearchVersion
      this.productsLoading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        search: query || ''
      }
      this.apiRequest('/spinning-wheel-campaigns/products', 'GET', data, (response) => {
        if (version !== this.productSearchVersion) return
        this.productsLoading = false
        if (!response.status) return
        let options = Array.isArray(response.data) ? response.data : []
        if (query) {
          const q = String(query).toLowerCase()
          options = options.filter((p) => String(p.name || '').toLowerCase().includes(q))
        }
        const selectedId = this.rewardForm.catalogproduct_id
        if (selectedId && !options.some((p) => Number(p.id) === Number(selectedId))) {
          const existing = this.productOptions.find((p) => Number(p.id) === Number(selectedId))
          if (existing) options = [existing].concat(options)
        }
        this.productOptions = options.slice(0, 50)
      })
    },
    saveReward () {
      if (this.rewardSaving) return
      if (this.rewardForm.type !== 1 && (!Number.isFinite(Number(this.rewardForm.winning_percentage)) || this.rewardForm.winning_percentage < 0.0001)) {
        swal('', this.$t('spinningWheel.percentageRequired'), 'error')
        return
      }
      if (this.rewardForm.type === 2 && (!this.rewardForm.loyalty_points || this.rewardForm.loyalty_points < 1)) {
        swal('', this.$t('spinningWheel.loyaltyPointsRequired'), 'error')
        return
      }
      if (this.rewardForm.type === 3 && (!this.rewardForm.discount || this.rewardForm.discount <= 0)) {
        swal('', this.$t('spinningWheel.discountRequired'), 'error')
        return
      }
      if (this.rewardForm.type === 4 && !this.rewardForm.catalogproduct_id) {
        swal('', this.$t('spinningWheel.productRequired'), 'error')
        return
      }
      const body = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        type: this.rewardForm.type,
        winning_percentage: this.rewardForm.winning_percentage,
        display_title: this.rewardForm.display_title,
        display_description: this.rewardForm.display_description,
        loyalty_points: this.rewardForm.type === 2 ? this.rewardForm.loyalty_points : null,
        coupon_type: this.rewardForm.type === 3 ? this.rewardForm.coupon_type : null,
        discount: this.rewardForm.type === 3 ? this.rewardForm.discount : null,
        usage_type: this.rewardForm.type === 3 ? this.rewardForm.usage_type : 'PRODUCTS',
        catalogproduct_id: this.rewardForm.type === 4 ? this.rewardForm.catalogproduct_id : null,
        sort_order: this.rewardForm.sort_order || 0,
        active: this.rewardForm.active ? 1 : 0
      }
      if (!this.campaignId) {
        const row = { ...body, id: this.editingReward ? this.editingReward.id : this.nextDraftId-- }
        if (this.rewardForm.type === 4) {
          row.product = this.productOptions.find(product => Number(product.id) === Number(row.catalogproduct_id))
        }
        const rows = this.editingReward
          ? this.rewards.map(item => item.id === row.id ? row : item)
          : this.rewards.concat(row)
        if (this.setDraftRewards(rows)) this.rewardDialogVisible = false
        return
      }
      this.rewardSaving = true
      const done = (response) => {
        this.rewardSaving = false
        if (!response.status) {
          const msg = this.flattenErrors(response.errors) || this.$t('validations.wentWrong')
          swal('', msg, 'error')
          return
        }
        this.rewardDialogVisible = false
        if (response.campaign) this.applyCampaignMeta(response.campaign)
        else this.loadCampaign()
      }
      if (this.editingReward && this.editingReward.id) {
        this.apiRequest('/spinning-wheel-rewards/' + this.editingReward.id, 'PUT', body, done)
      } else {
        this.apiRequest('/spinning-wheel-campaigns/' + this.campaignId + '/rewards', 'POST', body, done)
      }
    },
    onSortOrderInput (row) {
      if (!this.campaignId) return
      let val = Number(row.sort_order)
      if (Number.isNaN(val) || val < 0) val = 0
      if (val > 1000) val = 1000
      row.sort_order = val
      this.pendingSortUpdates[row.id] = val
      if (this.sortOrderDebounce) clearTimeout(this.sortOrderDebounce)
      this.sortOrderDebounce = setTimeout(() => this.flushSortOrderUpdates(), 800)
    },
    flushSortOrderUpdates () {
      const updates = Object.assign({}, this.pendingSortUpdates)
      this.pendingSortUpdates = {}
      this.sendRewardUpdates(updates, 'sort_order')
    },
    sendRewardUpdates (updates, field) {
      const ids = Object.keys(updates)
      const next = () => {
        const id = ids.shift()
        if (!id) {
          this.loadCampaign()
          return
        }
        const body = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany'),
          [field]: updates[id]
        }
        this.apiRequest('/spinning-wheel-rewards/' + id, 'PUT', body, response => {
          if (!response.status) {
            swal('', this.flattenErrors(response.errors) || this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
          next()
        })
      }
      if (ids.length) next()
    },
    onPercentageInput (row) {
      if (!this.campaignId) {
        if (!this.setDraftRewards(this.rewards)) row.winning_percentage = row._savedWinningPercentage
        return
      }
      const val = Number(row.winning_percentage)
      if (Number.isNaN(val) || val <= 0) return
      this.pendingPercentageUpdates[row.id] = val
      if (this.percentageDebounce) clearTimeout(this.percentageDebounce)
      this.percentageDebounce = setTimeout(() => this.flushPercentageUpdates(), 800)
    },
    flushPercentageUpdates () {
      const updates = Object.assign({}, this.pendingPercentageUpdates)
      this.pendingPercentageUpdates = {}
      this.sendRewardUpdates(updates, 'winning_percentage')
    },
    duplicateReward (row) {
      if (!row || !row.id || Number(row.type) === 1) return
      if (!this.campaignId) {
        this.setDraftRewards(this.rewards.concat({ ...row, id: this.nextDraftId--, sort_order: Number(row.sort_order) + 1 }))
        return
      }
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/spinning-wheel-rewards/' + row.id + '/duplicate', 'POST', data, (response) => {
        if (!response.status) {
          swal('', this.flattenErrors(response.errors) || this.$t('validations.wentWrong'), 'error')
          return
        }
        if (response.campaign) this.applyCampaignMeta(response.campaign)
        else this.loadCampaign()
      })
    },
    deleteReward (row) {
      swal({
        title: this.$t('spinningWheel.confirmDelete'),
        useRejections: false,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no')
      }).then((result) => {
        if (!result || !result.value) return
        if (!this.campaignId) {
          this.setDraftRewards(this.rewards.filter(item => item.id !== row.id), Number(row.type) !== 1)
          return
        }
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/spinning-wheel-rewards/' + row.id, 'DELETE', data, (response) => {
          if (response.status) {
            if (response.campaign) this.applyCampaignMeta(response.campaign)
            else this.loadCampaign()
          } else swal('', this.flattenErrors(response.errors) || this.$t('validations.wentWrong'), 'error')
        })
      })
    },
    openGeneratedCampaign (row) {
      const id = row.open_campaign_id || row.generated_campaign_id
      if (!id) return
      this.$router.push('/marketing/campaigns/edit/' + id)
    }
  }
}
</script>

<style>
.spinning-wheel-campaign-form .reward-active-toggle--disabled {
  pointer-events: none;
  opacity: 0.6;
}
.spinning-wheel-campaign-form .spinning-wheel-rewards-table .centered-cell .cell {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
}
.spinning-wheel-campaign-form .arrow-box {
  display: inline-block;
  font-size: 18px;
  padding: 4px 6px;
  border: 1px solid #674BD2 !important;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  user-select: none;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
  color: #674BD2;
}
.spinning-wheel-campaign-form .arrow-box:hover {
  background-color: #674BD2 !important;
  border-color: #674BD2 !important;
  color: #fff !important;
}
.spinning-wheel-campaign-form .custom-dropdown-menu {
  min-width: 180px;
  font-size: 14px;
}
.spinning-wheel-campaign-form .alert-info,
.spinning-wheel-reward-dialog .alert-info {
  background: #eef6ff;
  border: 1px solid #cfe2ff;
  padding: 10px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
}

/* Above GlobalModal (z-index 99999) */
.spinning-wheel-reward-dialog {
  max-width: calc(100vw - 48px);
  margin-bottom: 24px;
}
.spinning-wheel-reward-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #9a9a9a;
}
.spinning-wheel-reward-dialog .el-dialog__headerbtn:hover .el-dialog__close {
  color: #666;
}
.spinning-wheel-reward-dialog .el-dialog__body {
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  padding-top: 12px;
  padding-bottom: 8px;
}
.spinning-wheel-reward-dialog__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/SpinningWheel/SpinningWheelCampaignForm.vue